import React, { useState } from "react";
import { Alert, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useRouteLoaderData } from "react-router-dom";

import { useAppContext } from "@/App";
import { DEVELOPER_VKPLAY_GAMES_ID } from "@/const";
import { useRequest } from "@components/AppHook";
import { DevForm } from "@components/Forms/Fields";
import { Awesome, DevModal, YesNoColumn } from "@components/Helpers";
import { money_fmt, url_reverse } from "@components/Utils";

export function NothingFilteredAlert() {
    const { t } = useTranslation();
    const { apiData } = useAppContext();
    return (
        apiData.is_filtering &&
        apiData.object_list?.length === 0 && (
            <Alert variant="danger" className="mt-3">
                {t("Нет данных за этот период")}
            </Alert>
        )
    );
}

export function OrdersSummary() {
    const { t } = useTranslation();
    const { apiData } = useAppContext();
    return (
        apiData.stats_summary?.by_currency?.length > 0 && (
            <div className="mt-3">
                <div className="jumbotron p-3 mb-0">
                    <h4>{t("Итого за период")}:</h4>
                    <div>
                        {apiData.stats_summary?.by_currency.map((s, ind) => (
                            <div key={`stats_summary_${ind}`}>
                                <strong>{s.currency}:</strong> {money_fmt(s.amount)} ({s.count})
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )
    );
}

export function PaymentsSummary() {
    const { t } = useTranslation();
    const { apiData } = useAppContext();
    return (
        apiData.stats_summary?.length > 0 && (
            <div className="mt-3">
                <div className="jumbotron p-3 mb-0">
                    <h4>{t("Итого за период")}:</h4>
                    <div>
                        {apiData.stats_summary.map((s, ind) => (
                            <div key={`stats_summary_${ind}`}>
                                <strong>{s.currency}:</strong> {money_fmt(s.amount)} ({s.count})
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )
    );
}

export function OrdersTable({ forGame, devId }) {
    const { t } = useTranslation();
    const { apiData } = useAppContext();
    const commonInfo = useRouteLoaderData("commonInfo");

    return (
        apiData.object_list?.length > 0 && (
            <>
                <Table hover responsive className="dmg-table mt-4">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>{t("Дата/время")}</th>
                            {!forGame && <th>{t("Игра")}</th>}
                            <th>{t("Цена продажи")}</th>
                            <th>{t("Валюта")}</th>
                            <th>{t("Страна")}</th>
                            {forGame && commonInfo.account.is_super && <th>{t("User ID")}</th>}
                            {devId === DEVELOPER_VKPLAY_GAMES_ID && <th>{t("Себестоимость ключа")}</th>}
                            <th>{t("Тип платежа")}</th>
                            <th>{t("Ключ")}</th>
                            <th>{t("Возврат")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {apiData.object_list.map((obj) => (
                            <tr key={obj.id}>
                                <td>{obj.id}</td>
                                <td>{obj.date_payed}</td>
                                {!forGame && (
                                    <td>
                                        <Link to={url_reverse("games:statistics:orders", { game_id: obj.game_id })}>
                                            {obj.game_name}
                                        </Link>
                                    </td>
                                )}
                                <td>{obj.payment_sum}</td>
                                <td>{obj.currency}</td>
                                <td>{obj.vat_country || obj.__rb_country_human}</td>
                                {forGame && commonInfo.account.is_super && <td>{obj.buyer_user_id}</td>}
                                {devId === DEVELOPER_VKPLAY_GAMES_ID && <td>{obj.prime_code_price}</td>}
                                <td>{obj.payment_type}</td>
                                <td>{obj.serial}</td>
                                <td>{obj.is_refund && <YesNoColumn attr={obj.is_refund} />}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </>
        )
    );
}

export function PaymentsTable({ forGame, forMarket, showRefundButton = false }) {
    const { t } = useTranslation();
    const { apiData, setApiData, apiUrl } = useAppContext();
    const [paymentResp, setPaymentResp] = useState(null);
    const [returnMoney, setReturnMoney] = useState(null);
    const { api_post } = useRequest();

    const dmrRefundBtn = (ind, obj) => {
        if (!obj.__can_refund_dmr) {
            return;
        }
        return (
            <DevForm.Button size="sm" variant="warning" name="refund_dmr" onClick={() => setReturnMoney(ind)}>
                {t("Возврат DMR")}
            </DevForm.Button>
        );
    };

    const handleReturnMoney = (ev) => {
        const paymentInfo = apiData.object_list[returnMoney - 1];
        api_post(ev, apiUrl, {
            params: {
                [ev.target.name]: ev.target.value,
                payment_id: paymentInfo.id,
            },
            then: (resp) => {
                if (resp.status === "ok") {
                    const newApiData = { ...apiData };
                    newApiData.object_list[returnMoney - 1].is_refund = true;
                    setApiData(newApiData);
                    setReturnMoney(null);
                }
            },
        });
    };

    return (
        apiData.object_list?.length > 0 && (
            <>
                <Table hover responsive className="dmg-table mt-4">
                    <thead>
                        <tr>
                            <th>{t("Дата платежа")}</th>
                            {!forGame && <th>{t("Игра")}</th>}
                            {forMarket && (
                                <>
                                    <th>Email</th>
                                    <th>{t("Компания")}</th>
                                    <th>{t("Транзакция")}</th>
                                    <th>{t("Тип платежа")}</th>
                                </>
                            )}
                            <th>{t("Сумма")}</th>
                            <th>{t("Валюта")}</th>
                            {!forMarket && (
                                <>
                                    <th>{t("Страна")}</th>
                                    <th>User ID</th>
                                    <th>{t("ID транзакции")}</th>
                                </>
                            )}
                            <th>{t("Возврат")}</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {apiData.object_list?.map((obj, ind) => {
                            const style = obj.is_refund ? { backgroundColor: "rgb(222, 222, 222)" } : {};
                            return (
                                <tr key={obj.id} style={style}>
                                    <td>{obj.date_payed}</td>
                                    {!forGame && (
                                        <td>
                                            <Link
                                                to={url_reverse("games:statistics:payments", {
                                                    game_id: obj.game.id,
                                                })}
                                            >
                                                {obj.game.base_locale_object.name}
                                            </Link>
                                        </td>
                                    )}
                                    {forMarket && (
                                        <>
                                            <td>{obj.email}</td>
                                            <td>{obj.developer.name}</td>
                                            <td>{obj.transaction_id}</td>
                                            <td>{obj.payment_type}</td>
                                        </>
                                    )}
                                    <td>{obj.payment_sum}</td>
                                    <td>{obj.currency}</td>
                                    {!forMarket && (
                                        <>
                                            <td>{obj.vat_country || obj.__rb_country_human}</td>
                                            <td>{obj.pers_id}</td>
                                            <td>{obj.transaction_id}</td>
                                        </>
                                    )}
                                    <td>
                                        {obj.is_refund && <YesNoColumn attr={obj.is_refund} />}
                                        {showRefundButton && !obj.is_refund && dmrRefundBtn(ind + 1, obj)}
                                    </td>
                                    <td>
                                        {obj.response && (
                                            <a type="button" onClick={() => setPaymentResp(obj.response)}>
                                                <Awesome type="info-circle" color="#2d7dd0" />
                                            </a>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>

                <DevModal
                    show={returnMoney}
                    onHide={() => setReturnMoney(null)}
                    header={t("Подтверждение действия")}
                    body={t("Вернуть деньги по транзакции? Действие невозможно отменить.")}
                    footer={
                        <DevForm.Button
                            variant="primary"
                            name="action"
                            value="refund_payment"
                            onClick={handleReturnMoney}
                        >
                            {t("Вернуть деньги")}
                        </DevForm.Button>
                    }
                />

                <DevModal
                    size="lg"
                    show={paymentResp}
                    onHide={() => setPaymentResp(null)}
                    header={t("Ответ партнера")}
                    body={<pre>{JSON.stringify(JSON.parse(paymentResp), null, 2)}</pre>}
                />
            </>
        )
    );
}
